import { BladeFile } from '@razorpay/blade/components';
import { ApiClaimResponse } from './constant';
import { CreateClaimStateType } from '../Create/constant';
import { paiseAmountToRupee } from 'utils/Numbers';

/**
 * API payload format for creating/updating claims
 */
interface ApiClaimPayload {
  component_id: string;
  claim_amount: number;
  expense_date: number;
  claim_description: string;
  claim_evidence_id: string[];
  claim_id?: string;
}

/**
 * Converts claim amount from paise to rupees
 * @param amountInPaise - Amount in paise
 * @returns Amount in rupees as string
 */
export const convertPaiseToRupees = (amountInPaise: number): string => {
  return (amountInPaise / 100).toString();
};

/**
 * Converts claim amount from rupees to paise
 * @param amountInRupees - Amount in rupees as string
 * @returns Amount in paise
 */
export const convertRupeesToPaise = (amountInRupees: string): number => {
  return Math.round(parseFloat(amountInRupees) * 100);
};

/**
 * Maps component ID to dropdown value
 * This should be replaced with actual API call or mapping logic
 * @param componentId - Component ID from API (e.g., "cmp_medical_allowance_123")
 * @returns Dropdown value (e.g., "medical")
 */
export const getComponentDropdownValue = (componentId: string): string => {
  // TODO: Replace with actual mapping logic from component_id to dropdown value
  // This could be from a component lookup API or a static mapping
  const idLower = componentId.toLowerCase();
  if (idLower.includes('medical')) return 'medical';
  if (idLower.includes('travel')) return 'travel';
  if (idLower.includes('food')) return 'food';
  if (idLower.includes('internet')) return 'internet';
  return 'medical'; // default fallback
};

/**
 * Converts timestamp to ISO date string for DatePicker
 * @param timestamp - Timestamp in milliseconds
 * @returns ISO date string (YYYY-MM-DD format)
 */
export const convertTimestampToDateString = (timestamp: number): string => {
  const date = new Date(timestamp);
  return date.toISOString().split('T')[0];
};

/**
 * Converts API claim response to CreateClaimInitialState format
 * This allows reusing the CreateModal component for editing claims
 *
 * @param apiClaim - Claim data from API in ApiClaimResponse format
 * @param existingFiles - Optional array of BladeFile objects for existing evidence
 * @returns Form initial state object compatible with CreateClaimInitialState
 *
 * @example
 * const apiResponse = {
 *   claim_id: "claim_abc123xyz789",
 *   component_id: "cmp_medical_allowance_123",
 *   claim_amount: 750000,
 *   expense_date: 1703123456789,
 *   claim_description: "Medical bill",
 *   claim_evidence_id: ["ufh_file_1", "ufh_file_2"],
 *   // ... other fields
 * };
 *
 * const formState = convertApiClaimToFormState(apiResponse);
 * // Returns:
 * // {
 * //   Component_id: "cmp_medical_allowance_123",
 * //   Component_name: "medical",
 * //   Bill_amount: 7500,
 * //   Expense_date: "2023-12-21",
 * //   Description: "Medical bill",
 * //   Files: []
 * // }
 */
export const convertApiClaimToFormState = (
  apiClaim: ApiClaimResponse,
  existingFiles: BladeFile[] = [],
) => {
  return {
    Component_id: apiClaim.component_id,
    Component_name: getComponentDropdownValue(apiClaim.component_id),
    Bill_amount: paiseAmountToRupee(apiClaim.claim_amount),
    Expense_date: convertTimestampToDateString(apiClaim.expense_date),
    Description: apiClaim.claim_description,
    Files: existingFiles,
  };
};

/**
 * Converts form state back to API format for update request
 * @param formData - Form state data from CreateModal
 * @param claimId - Claim ID for update (only needed for edit)
 * @returns Claim data in API format
 *
 * @example
 * const formData = {
 *   Component_id: "cmp_medical_allowance_123",
 *   Component_name: "medical",
 *   Bill_amount: 7500,
 *   Expense_date: "2023-12-21",
 *   Description: "Medical bill",
 *   Files: [bladeFile1, bladeFile2]
 * };
 *
 * const apiPayload = convertFormStateToApiClaim(formData, "claim_abc123");
 * // Returns:
 * // {
 * //   claim_id: "claim_abc123",
 * //   component_id: "cmp_medical_allowance_123",
 * //   claim_amount: 750000,
 * //   expense_date: 1703123456789,
 * //   claim_description: "Medical bill",
 * //   claim_evidence_id: ["ufh_file_1", "ufh_file_2"]
 * // }
 */
export const convertFormStateToApiClaim = (
  formData: CreateClaimStateType,
  claimId?: string,
): ApiClaimPayload => {
  const payload: ApiClaimPayload = {
    component_id: formData.Component_id,
    claim_amount: convertRupeesToPaise(formData.Bill_amount),
    expense_date: new Date(formData.Expense_date).getTime(),
    claim_description: formData.Description,
    claim_evidence_id: (formData.Files as (BladeFile & { id?: string })[])
      .map((file) => file.id)
      .filter((id): id is string => Boolean(id)),
  };

  // Add claim_id only for edit operations
  if (claimId) {
    payload.claim_id = claimId;
  }

  return payload;
};
